"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useServerInsertedHTML } from "next/navigation";
import {
  FluentProvider,
  SSRProvider,
  RendererProvider,
  createDOMRenderer,
  renderToStyleElements,
} from "@fluentui/react-components";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { lightTheme, darkTheme } from "./fluent-theme";

export type ThemeMode = "light" | "dark";
export type ThemePreference = "light" | "dark" | "system";

type ThemeContextValue = {
  /** The resolved palette actually in effect (system → light/dark). */
  mode: ThemeMode;
  /** The user's stored choice, which may be "system". */
  preference: ThemePreference;
  setMode: (mode: ThemeMode) => void;
  setPreference: (preference: ThemePreference) => void;
  toggle: () => void;
};

const THEME_STORAGE_KEY = "theme";

function systemMode(): ThemeMode {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveMode(pref: ThemePreference): ThemeMode {
  return pref === "system" ? systemMode() : pref;
}

// Default to "light" on the server so SSR matches the pre-paint script
// (which defaults to the cream/light editorial theme unless the user
// explicitly chose dark). The inline pre-paint script in <head> updates
// `data-theme` *before* React hydrates, and then the useEffect below syncs
// React state to whatever was set.
const ThemeContext = createContext<ThemeContextValue>({
  mode: "light",
  preference: "system",
  setMode: () => {},
  setPreference: () => {},
  toggle: () => {},
});

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [renderer] = useState(() => createDOMRenderer());
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, refetchOnWindowFocus: false },
        },
      }),
  );
  const [preference, setPreferenceState] = useState<ThemePreference>("system");
  const [mode, setModeState] = useState<ThemeMode>("light");

  // Apply a resolved palette to the DOM + React state. Centralised so the
  // boot-sync, the explicit setters and the OS-change listener all paint the
  // same way.
  const applyMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    document.documentElement.dataset.theme = next;
  }, []);

  // Sync React state with whatever the pre-paint inline script chose, so
  // useTheme consumers reflect the real DOM state after hydration. Mirrors the
  // boot-script logic: the stored value is a preference; "system" (or none)
  // resolves to the OS scheme.
  useEffect(() => {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    const pref: ThemePreference =
      stored === "dark" ? "dark" : stored === "light" ? "light" : "system";
    setPreferenceState(pref);
    applyMode(resolveMode(pref));
  }, [applyMode]);

  // While the preference is "system", follow the OS as it changes live.
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyMode(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference, applyMode]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next);
      applyMode(resolveMode(next));
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // ignore quota errors / private mode
      }
    },
    [applyMode],
  );

  // Back-compat: an explicit light/dark set is just a preference of that name.
  const setMode = useCallback(
    (next: ThemeMode) => setPreference(next),
    [setPreference],
  );

  const toggle = useCallback(() => {
    setPreference(mode === "dark" ? "light" : "dark");
  }, [mode, setPreference]);

  useServerInsertedHTML(() => <>{renderToStyleElements(renderer)}</>);

  const ctx = useMemo<ThemeContextValue>(
    () => ({ mode, preference, setMode, setPreference, toggle }),
    [mode, preference, setMode, setPreference, toggle],
  );

  return (
    <RendererProvider renderer={renderer}>
      <SSRProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeContext.Provider value={ctx}>
            <FluentProvider theme={mode === "dark" ? darkTheme : lightTheme}>
              {children}
            </FluentProvider>
          </ThemeContext.Provider>
        </QueryClientProvider>
      </SSRProvider>
    </RendererProvider>
  );
}
